/**
 * ShippingAddressStep
 * --------------------------------------------------------------
 * Checkout step for collecting/selecting a shipping address when the
 * cart contains physical items. Loads saved addresses from
 * `store_buyer_addresses`, lets the buyer pick or add a new one,
 * persists to the address book, and bubbles the chosen address up to
 * the cart page so it can be sent to `store-create-checkout`.
 */
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, MapPin, Plus, Pencil } from "lucide-react";
import { toast } from "sonner";

export type ShippingAddress = {
  id?: string;
  recipient_name: string;
  phone?: string | null;
  line1: string;
  line2?: string | null;
  city: string;
  region?: string | null;
  postal_code: string;
  country: string; // ISO-2
};

const SUPPORTED_COUNTRIES: Array<{ code: string; name: string }> = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "GB", name: "United Kingdom" },
  { code: "AU", name: "Australia" },
];

const addressSchema = z.object({
  recipient_name: z.string().trim().min(1, "Required").max(120),
  phone: z.string().trim().max(40).optional().or(z.literal("")),
  line1: z.string().trim().min(1, "Required").max(200),
  line2: z.string().trim().max(200).optional().or(z.literal("")),
  city: z.string().trim().min(1, "Required").max(120),
  region: z.string().trim().max(120).optional().or(z.literal("")),
  postal_code: z.string().trim().min(1, "Required").max(20),
  country: z.enum(["US", "CA", "GB", "AU"], {
    errorMap: () => ({ message: "Choose a supported country" }),
  }),
  is_default: z.boolean().optional(),
});

type Saved = ShippingAddress & {
  id: string;
  label: string | null;
  is_default: boolean;
};

type Props = {
  selectedId: string | null;
  onSelect: (addr: ShippingAddress | null) => void;
};

type CountryCode = "US" | "CA" | "GB" | "AU";

type AddressForm = {
  recipient_name: string;
  phone: string;
  line1: string;
  line2: string;
  city: string;
  region: string;
  postal_code: string;
  country: CountryCode;
  is_default: boolean;
};

const blankForm: AddressForm = {
  recipient_name: "",
  phone: "",
  line1: "",
  line2: "",
  city: "",
  region: "",
  postal_code: "",
  country: "US",
  is_default: false,
};

const ShippingAddressStep = ({ selectedId, onSelect }: Props) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState<Saved[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<AddressForm>(blankForm);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const load = async () => {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("store_buyer_addresses")
      .select("*")
      .eq("user_id", user.id)
      .order("is_default", { ascending: false })
      .order("updated_at", { ascending: false });
    if (error) {
      console.error(error);
      toast.error("Couldn't load saved addresses");
    } else {
      setSaved((data ?? []) as Saved[]);
      const initial = data?.find((d) => d.is_default) ?? data?.[0];
      if (initial && !selectedId) {
        onSelect(toAddress(initial as Saved));
      }
      if (!data?.length) setShowForm(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const startEdit = (addr: Saved) => {
    setEditingId(addr.id);
    setForm({
      recipient_name: addr.recipient_name,
      phone: addr.phone ?? "",
      line1: addr.line1,
      line2: addr.line2 ?? "",
      city: addr.city,
      region: addr.region ?? "",
      postal_code: addr.postal_code,
      country: ((addr.country as CountryCode) || "US"),
      is_default: addr.is_default,
    });
    setShowForm(true);
  };

  const startNew = () => {
    setEditingId(null);
    setForm({ ...blankForm, is_default: saved.length === 0 });
    setShowForm(true);
  };

  const submit = async () => {
    if (!user) return;
    const parsed = addressSchema.safeParse(form);
    if (!parsed.success) {
      const flat = parsed.error.flatten().fieldErrors;
      setErrors(
        Object.fromEntries(
          Object.entries(flat).map(([k, v]) => [k, (v?.[0] as string) ?? ""]),
        ),
      );
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const payload = {
        user_id: user.id,
        recipient_name: parsed.data.recipient_name,
        phone: parsed.data.phone || null,
        line1: parsed.data.line1,
        line2: parsed.data.line2 || null,
        city: parsed.data.city,
        region: parsed.data.region || null,
        postal_code: parsed.data.postal_code,
        country: parsed.data.country,
        is_default: parsed.data.is_default ?? false,
      };

      // If marking default, clear previous default first
      if (payload.is_default) {
        await supabase
          .from("store_buyer_addresses")
          .update({ is_default: false })
          .eq("user_id", user.id)
          .neq("id", editingId ?? "00000000-0000-0000-0000-000000000000");
      }

      let saved: Saved | null = null;
      if (editingId) {
        const { data, error } = await supabase
          .from("store_buyer_addresses")
          .update(payload)
          .eq("id", editingId)
          .select("*")
          .single();
        if (error) throw error;
        saved = data as Saved;
      } else {
        const { data, error } = await supabase
          .from("store_buyer_addresses")
          .insert(payload)
          .select("*")
          .single();
        if (error) throw error;
        saved = data as Saved;
      }
      toast.success(editingId ? "Address updated" : "Address saved");
      setShowForm(false);
      setEditingId(null);
      await load();
      if (saved) onSelect(toAddress(saved));
    } catch (e) {
      console.error(e);
      toast.error((e as Error).message ?? "Couldn't save address");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading shipping addresses…
      </Card>
    );
  }

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          <h3 className="font-semibold">Shipping address</h3>
        </div>
        {!showForm && (
          <Button variant="outline" size="sm" onClick={startNew}>
            <Plus className="h-4 w-4 mr-1" /> Add new
          </Button>
        )}
      </div>

      {!showForm && saved.length > 0 && (
        <div className="mt-4 space-y-2">
          {saved.map((addr) => {
            const active = selectedId === addr.id;
            return (
              <button
                type="button"
                key={addr.id}
                onClick={() => onSelect(toAddress(addr))}
                className={`w-full text-left rounded-md border p-3 transition ${
                  active
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-foreground/30"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {addr.recipient_name}
                      {addr.is_default && (
                        <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                          Default
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-muted-foreground mt-0.5 truncate">
                      {addr.line1}
                      {addr.line2 ? `, ${addr.line2}` : ""}, {addr.city}
                      {addr.region ? `, ${addr.region}` : ""} {addr.postal_code},{" "}
                      {addr.country}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 flex-shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      startEdit(addr);
                    }}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {showForm && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field
            label="Recipient name"
            value={form.recipient_name}
            onChange={(v) => setForm({ ...form, recipient_name: v })}
            error={errors.recipient_name}
            className="sm:col-span-2"
          />
          <Field
            label="Phone (optional)"
            value={form.phone}
            onChange={(v) => setForm({ ...form, phone: v })}
            error={errors.phone}
            className="sm:col-span-2"
          />
          <Field
            label="Address line 1"
            value={form.line1}
            onChange={(v) => setForm({ ...form, line1: v })}
            error={errors.line1}
            className="sm:col-span-2"
          />
          <Field
            label="Address line 2 (optional)"
            value={form.line2}
            onChange={(v) => setForm({ ...form, line2: v })}
            error={errors.line2}
            className="sm:col-span-2"
          />
          <Field
            label="City"
            value={form.city}
            onChange={(v) => setForm({ ...form, city: v })}
            error={errors.city}
          />
          <Field
            label="State / Region"
            value={form.region}
            onChange={(v) => setForm({ ...form, region: v })}
            error={errors.region}
          />
          <Field
            label="Postal code"
            value={form.postal_code}
            onChange={(v) => setForm({ ...form, postal_code: v })}
            error={errors.postal_code}
          />
          <div className="space-y-1.5">
            <Label className="text-xs">Country</Label>
            <select
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={form.country}
              onChange={(e) =>
                setForm({ ...form, country: e.target.value as typeof form.country })
              }
            >
              {SUPPORTED_COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
            {errors.country && (
              <p className="text-xs text-destructive">{errors.country}</p>
            )}
          </div>

          <label className="sm:col-span-2 flex items-center gap-2 text-sm">
            <Checkbox
              checked={form.is_default}
              onCheckedChange={(v) =>
                setForm({ ...form, is_default: v === true })
              }
            />
            Set as default shipping address
          </label>

          <div className="sm:col-span-2 flex items-center gap-2 justify-end">
            {saved.length > 0 && (
              <Button
                variant="ghost"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                }}
              >
                Cancel
              </Button>
            )}
            <Button onClick={submit} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingId ? "Update address" : "Save address"}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

const Field = ({
  label,
  value,
  onChange,
  error,
  className,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  className?: string;
}) => (
  <div className={`space-y-1.5 ${className ?? ""}`}>
    <Label className="text-xs">{label}</Label>
    <Input value={value} onChange={(e) => onChange(e.target.value)} />
    {error && <p className="text-xs text-destructive">{error}</p>}
  </div>
);

const toAddress = (s: Saved): ShippingAddress => ({
  id: s.id,
  recipient_name: s.recipient_name,
  phone: s.phone,
  line1: s.line1,
  line2: s.line2,
  city: s.city,
  region: s.region,
  postal_code: s.postal_code,
  country: s.country,
});

export default ShippingAddressStep;
